import React from "react";
import { IconArrowRight, IconInfo, IconPlus, IconTrash } from "./icons";
import {
  FRACTIONS,
  FRACTION_CLASS,
  addTier,
  bandsOf,
  boundedTiers,
  canAddTier,
  describeTiers,
  fh,
  fractionLabel,
  minFractionFor,
  minFractionForOpen,
  normalizeFractions,
  openFraction,
  patchTier,
  removeTier,
  setOpenFraction,
  tierErrors,
} from "./policyUtils";
import styles from "./BillingPolicies.module.css";

const bandClass = (fraction) => styles[FRACTION_CLASS[Number(fraction)] || "free"];

/* The hour scale: 0h to 24h, one block per band, so the shape of a table is
   read before any of its numbers are. */
export const Ruler = ({ tiers }) => {
  const bands = bandsOf(tiers);
  const ticks = [0, ...bands.slice(0, -1).map((band) => band.to), 24];
  return (
    <div className={styles.ruler}>
      <div className={styles.rBar}>
        {bands.map((band, index) => (
          <div
            key={`${band.from}-${index}`}
            className={`${styles.rSeg} ${bandClass(band.fraction)}`}
            style={{ flexGrow: band.to - band.from }}
            title={`${fh(band.from)}–${fh(band.to)}h: ${fractionLabel(band.fraction)}`}
          >
            {band.to - band.from >= 2.5 ? fractionLabel(band.fraction) : ""}
          </div>
        ))}
      </div>
      {ticks.map((hour, index) => (
        <span
          key={`${hour}-${index}`}
          className={styles.rTick}
          style={{ left: `${(hour / 24) * 100}%` }}
        >
          {fh(hour)}h
        </span>
      ))}
    </div>
  );
};

/* Steps climb: each charges more than the one above it, so a choice that
   would not is greyed out with the reason. Once a step is a full day there is
   nothing left below it to climb to. */
const ChargePicker = ({ value, onChange, disabled, min, exhausted }) => (
  <div className={styles.charge}>
    {FRACTIONS.map(({ value: option, label }) => {
      const unreachable = exhausted || option < min;
      const off = disabled || unreachable;
      const selected = Number(value) === option;
      return (
        <button
          key={option}
          type="button"
          className={[
            bandClass(option),
            selected ? styles.on : "",
            unreachable && !disabled && !selected ? styles.no : "",
          ]
            .filter(Boolean)
            .join(" ")}
          disabled={off}
          title={
            off && !disabled
              ? exhausted
                ? "The step above is already a full day"
                : "Must charge more than the step above"
              : undefined
          }
          onClick={() => onChange(option)}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const RuleEditor = ({ tiers, onChange, readOnly }) => {
  const bounded = boundedTiers(tiers);
  const errors = tierErrors(tiers);
  const above = openFraction(tiers);

  const lastValidHours = bounded.reduce(
    (hours, tier, index) => (errors[index] ? hours : Number(tier.up_to_hours) || hours),
    0
  );
  const lastBounded = bounded[bounded.length - 1];
  const openExhausted = !!lastBounded && Number(lastBounded.fraction) === 1;

  return (
    <>
      <Ruler tiers={tiers} />

      <div className={styles.rules}>
        {bounded.map((tier, index) => (
          <div key={index} className={styles.rule}>
            <span className={`${styles.sw} ${bandClass(tier.fraction)}`} />

            <select
              className={styles.fi}
              value={tier.inclusive ? "incl" : "excl"}
              disabled={readOnly}
              aria-label="Boundary"
              onChange={(event) =>
                onChange(patchTier(tiers, index, { inclusive: event.target.value === "incl" }))
              }
            >
              <option value="incl">Up to</option>
              <option value="excl">Under</option>
            </select>

            <div className={`${styles.hIn} ${errors[index] ? styles.bad : ""}`}>
              <input
                inputMode="decimal"
                value={tier.up_to_hours === "" || tier.up_to_hours == null ? "" : tier.up_to_hours}
                disabled={readOnly}
                aria-label="Hours"
                onChange={(event) => {
                  const parsed = parseFloat(event.target.value);
                  onChange(
                    patchTier(tiers, index, {
                      up_to_hours: Number.isNaN(parsed) ? "" : parsed,
                    })
                  );
                }}
              />
              <span>h</span>
            </div>

            <span className={styles.arrow}>
              <IconArrowRight size={14} />
            </span>

            <ChargePicker
              value={tier.fraction}
              disabled={readOnly}
              min={minFractionFor(tiers, index)}
              exhausted={index > 0 && Number(bounded[index - 1].fraction) === 1}
              onChange={(fraction) =>
                onChange(normalizeFractions(patchTier(tiers, index, { fraction })))
              }
            />

            {readOnly ? (
              <span />
            ) : (
              <button
                type="button"
                className={styles.del}
                title="Remove this step"
                onClick={() => onChange(removeTier(tiers, index))}
              >
                <IconTrash size={15} />
              </button>
            )}

            {errors[index] && <div className={styles.rErr}>{errors[index]}</div>}
          </div>
        ))}

        <div className={styles.rule}>
          <span className={`${styles.sw} ${bandClass(above)}`} />
          <span className={styles.lbl} style={{ gridColumn: "2 / 4" }}>
            {bounded.length ? `Over ${fh(lastValidHours)}h` : "Any stay"}
          </span>
          <span className={styles.arrow}>
            <IconArrowRight size={14} />
          </span>
          <ChargePicker
            value={above}
            disabled={readOnly}
            min={minFractionForOpen(tiers)}
            exhausted={openExhausted}
            onChange={(fraction) => onChange(setOpenFraction(tiers, fraction))}
          />
          <span />
        </div>
      </div>

      {!readOnly &&
        (canAddTier(tiers) ? (
          <button type="button" className={styles.addRule} onClick={() => onChange(addTier(tiers))}>
            <IconPlus size={15} width={2.4} />
            Add a step
          </button>
        ) : (
          <div className={`${styles.addRule} ${styles.addRuleOff}`}>
            <IconInfo size={15} />
            {openExhausted
              ? "The last step is already a full day — nothing below it can charge more"
              : "No room for another step — each one has to charge more than the one above"}
          </div>
        ))}

      <div className={styles.plain}>
        <IconInfo size={15} />
        <span>{describeTiers(tiers)}</span>
      </div>
    </>
  );
};

export default RuleEditor;
